/**
 * Comprehensive error logging utility for the E-Payment Stablecoin application
 * Provides structured logging, error tracking, and user-friendly error reporting
 */

export enum ErrorCategory {
  WALLET_CONNECTION = 'WALLET_CONNECTION',
  BALANCE_FETCH = 'BALANCE_FETCH',
  NETWORK_SWITCH = 'NETWORK_SWITCH',
  TOKEN_VALIDATION = 'TOKEN_VALIDATION',
  SWAP_TRANSACTION = 'SWAP_TRANSACTION',
  PROVIDER_DISCONNECT = 'PROVIDER_DISCONNECT',
  BSC_RPC_ERROR = 'BSC_RPC_ERROR',
  BSC_GAS_ERROR = 'BSC_GAS_ERROR',
  BSC_TRANSACTION_ERROR = 'BSC_TRANSACTION_ERROR',
  CROSS_CHAIN_ERROR = 'CROSS_CHAIN_ERROR',
  TOKEN_CONTRACT_ERROR = 'TOKEN_CONTRACT_ERROR',
  UNKNOWN = 'UNKNOWN'
}

export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface ErrorLog {
  id: string;
  timestamp: number;
  category: ErrorCategory;
  severity: ErrorSeverity;
  message: string;
  userMessage: string;
  context?: any;
  stack?: string;
  userAgent?: string;
  walletAddress?: string;
  networkId?: number;
  retryable: boolean;
  retryCount?: number;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 1000;

  /**
   * Log an error with comprehensive details
   */
  logError(
    category: ErrorCategory,
    severity: ErrorSeverity,
    message: string,
    userMessage: string,
    context?: any,
    error?: Error,
    retryable = false,
    retryCount = 0
  ): string {
    const errorId = this.generateId();
    
    const errorLog: ErrorLog = {
      id: errorId,
      timestamp: Date.now(),
      category,
      severity,
      message,
      userMessage,
      context: this.sanitizeContext(context),
      stack: error?.stack,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      retryable,
      retryCount
    };

    // Add to logs array
    this.logs.unshift(errorLog);
    
    // Maintain max logs limit
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Console logging based on severity
    this.consoleLog(errorLog, error);

    // In production, you might want to send critical errors to a logging service
    if (severity === ErrorSeverity.CRITICAL) {
      this.reportCriticalError(errorLog);
    }

    return errorId;
  }

  /**
   * Log wallet connection errors
   */
  logWalletError(
    walletName: string,
    error: any,
    userMessage: string,
    retryable = false,
    retryCount = 0
  ): string {
    return this.logError(
      ErrorCategory.WALLET_CONNECTION,
      ErrorSeverity.MEDIUM,
      `Wallet connection failed for ${walletName}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { walletName, errorCode: error?.code },
      error,
      retryable,
      retryCount
    );
  }

  /**
   * Log balance fetching errors
   */
  logBalanceError(
    tokenSymbol: string,
    error: any,
    userMessage: string,
    retryable = true,
    retryCount = 0
  ): string {
    return this.logError(
      ErrorCategory.BALANCE_FETCH,
      ErrorSeverity.LOW,
      `Balance fetch failed for ${tokenSymbol}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { tokenSymbol, errorCode: error?.code },
      error,
      retryable,
      retryCount
    );
  }

  /**
   * Log network switching errors
   */
  logNetworkError(
    networkId: number,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.NETWORK_SWITCH,
      ErrorSeverity.MEDIUM,
      `Network switch failed for chain ${networkId}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { networkId, errorCode: error?.code },
      error,
      retryable
    );
  }

  /**
   * Log swap transaction errors
   */
  logSwapError(
    fromToken: string,
    toToken: string,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.SWAP_TRANSACTION,
      ErrorSeverity.HIGH,
      `Swap transaction failed (${fromToken} → ${toToken}): ${error?.message || 'Unknown error'}`,
      userMessage,
      { fromToken, toToken, errorCode: error?.code },
      error,
      retryable
    );
  }

  /**
   * Log provider disconnection events
   */
  logProviderDisconnect(
    reason: string,
    userMessage: string
  ): string {
    return this.logError(
      ErrorCategory.PROVIDER_DISCONNECT,
      ErrorSeverity.MEDIUM,
      `Provider disconnected: ${reason}`,
      userMessage,
      { reason },
      undefined,
      false
    );
  }

  /**
   * Log BSC-specific RPC errors
   */
  logBSCRpcError(
    method: string,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.BSC_RPC_ERROR,
      ErrorSeverity.MEDIUM,
      `BSC RPC error for method ${method}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { method, errorCode: error?.code, chainId: 56 },
      error,
      retryable
    );
  }

  /**
   * Log BSC gas-related errors
   */
  logBSCGasError(
    operation: string,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.BSC_GAS_ERROR,
      ErrorSeverity.HIGH,
      `BSC gas error during ${operation}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { operation, errorCode: error?.code, chainId: 56 },
      error,
      retryable
    );
  }

  /**
   * Log BSC transaction errors
   */
  logBSCTransactionError(
    transactionHash: string,
    error: any,
    userMessage: string,
    retryable = false
  ): string {
    return this.logError(
      ErrorCategory.BSC_TRANSACTION_ERROR,
      ErrorSeverity.HIGH,
      `BSC transaction failed ${transactionHash}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { transactionHash, errorCode: error?.code, chainId: 56 },
      error,
      retryable
    );
  }

  /**
   * Log cross-chain operation errors
   */
  logCrossChainError(
    fromChain: number,
    toChain: number,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.CROSS_CHAIN_ERROR,
      ErrorSeverity.HIGH,
      `Cross-chain operation failed (${fromChain} → ${toChain}): ${error?.message || 'Unknown error'}`,
      userMessage,
      { fromChain, toChain, errorCode: error?.code },
      error,
      retryable
    );
  }

  /**
   * Log token contract errors
   */
  logTokenContractError(
    tokenSymbol: string,
    contractAddress: string,
    chainId: number,
    error: any,
    userMessage: string,
    retryable = true
  ): string {
    return this.logError(
      ErrorCategory.TOKEN_CONTRACT_ERROR,
      ErrorSeverity.MEDIUM,
      `Token contract error for ${tokenSymbol} (${contractAddress}) on chain ${chainId}: ${error?.message || 'Unknown error'}`,
      userMessage,
      { tokenSymbol, contractAddress, chainId, errorCode: error?.code },
      error,
      retryable
    );
  }

  /**
   * Get error logs by category
   */
  getLogsByCategory(category: ErrorCategory): ErrorLog[] {
    return this.logs.filter(log => log.category === category);
  }

  /**
   * Get recent error logs
   */
  getRecentLogs(count = 50): ErrorLog[] {
    return this.logs.slice(0, count);
  }

  /**
   * Get error statistics
   */
  getErrorStats(): { [key in ErrorCategory]: number } {
    const stats = Object.values(ErrorCategory).reduce((acc, category) => {
      acc[category] = 0;
      return acc;
    }, {} as { [key in ErrorCategory]: number });

    this.logs.forEach(log => {
      stats[log.category]++;
    });

    return stats;
  }

  /**
   * Clear old logs (older than specified days)
   */
  clearOldLogs(daysOld = 7): void {
    const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
    this.logs = this.logs.filter(log => log.timestamp > cutoffTime);
  }

  /**
   * Export logs for debugging
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  private generateId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private sanitizeContext(context: any): any {
    if (!context) return undefined;
    
    // Remove sensitive information
    const sanitized = { ...context };
    
    // Remove private keys, mnemonics, etc.
    if (sanitized.privateKey) delete sanitized.privateKey;
    if (sanitized.mnemonic) delete sanitized.mnemonic;
    if (sanitized.password) delete sanitized.password;
    
    return sanitized;
  }

  private consoleLog(errorLog: ErrorLog, originalError?: Error): void {
    const logMessage = `[${errorLog.category}] ${errorLog.message}`;
    
    switch (errorLog.severity) {
      case ErrorSeverity.CRITICAL:
        console.error('🚨 CRITICAL:', logMessage, originalError);
        break;
      case ErrorSeverity.HIGH:
        console.error('🔴 ERROR:', logMessage, originalError);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('🟡 WARNING:', logMessage, originalError);
        break;
      case ErrorSeverity.LOW:
        console.info('🔵 INFO:', logMessage, originalError);
        break;
      default:
        console.log('⚪ LOG:', logMessage, originalError);
    }
  }

  private reportCriticalError(errorLog: ErrorLog): void {
    // In a production environment, you would send this to your error reporting service
    // For example: Sentry, Bugsnag, LogRocket, etc.
    
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      // Example: Send to error reporting service
      // errorReportingService.captureException(errorLog);
      console.error('Critical error reported:', errorLog);
    }
  }
}

// Singleton instance
export const errorLogger = new ErrorLogger();

/**
 * Helper function for quick error logging
 */
export const logError = (
  category: ErrorCategory,
  severity: ErrorSeverity,
  message: string,
  userMessage: string,
  context?: any,
  error?: Error,
  retryable = false,
  retryCount = 0
): string => {
  return errorLogger.logError(category, severity, message, userMessage, context, error, retryable, retryCount);
};

/**
 * Provider disconnection and account change handler
 */
export class ProviderEventHandler {
  private static instance: ProviderEventHandler;
  private listeners: Set<(account: string | null) => void> = new Set();
  private currentAccount: string | null = null;

  static getInstance(): ProviderEventHandler {
    if (!ProviderEventHandler.instance) {
      ProviderEventHandler.instance = new ProviderEventHandler();
    }
    return ProviderEventHandler.instance;
  }

  /**
   * Initialize provider event listeners
   */
  initialize(): void {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    const ethereum = window.ethereum as any;

    // Handle account changes
    ethereum.on?.('accountsChanged', (accounts: string[]) => {
      const newAccount = accounts.length > 0 ? accounts[0] : null;
      
      if (newAccount !== this.currentAccount) {
        const previousAccount = this.currentAccount;
        this.currentAccount = newAccount;
        
        // Log the account change
        errorLogger.logError(
          ErrorCategory.PROVIDER_DISCONNECT,
          ErrorSeverity.MEDIUM,
          `Account changed from ${previousAccount} to ${newAccount}`,
          newAccount ? 'Wallet account changed' : 'Wallet disconnected',
          { previousAccount, newAccount },
          undefined,
          false
        );

        // Notify listeners
        this.listeners.forEach(listener => listener(newAccount));
      }
    });

    // Handle chain changes
    ethereum.on?.('chainChanged', (chainId: string) => {
      errorLogger.logError(
        ErrorCategory.NETWORK_SWITCH,
        ErrorSeverity.LOW,
        `Chain changed to ${chainId}`,
        'Network changed',
        { chainId },
        undefined,
        false
      );
      
      // Reload page to reset state (recommended by MetaMask)
      window.location.reload();
    });

    // Handle disconnection
    ethereum.on?.('disconnect', (error: any) => {
      errorLogger.logProviderDisconnect(
        error?.message || 'Provider disconnected',
        'Wallet connection lost. Please reconnect your wallet.'
      );
      
      this.currentAccount = null;
      this.listeners.forEach(listener => listener(null));
    });
  }

  /**
   * Add account change listener
   */
  onAccountChange(listener: (account: string | null) => void): () => void {
    this.listeners.add(listener);
    
    // Return cleanup function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Clean up event listeners
   */
  cleanup(): void {
    if (typeof window === 'undefined' || !window.ethereum) {
      return;
    }

    const ethereum = window.ethereum as any;
    
    // Remove all listeners
    ethereum.removeListener?.('accountsChanged', () => {});
    ethereum.removeListener?.('chainChanged', () => {});
    ethereum.removeListener?.('disconnect', () => {});
    
    this.listeners.clear();
  }
}

export const providerEventHandler = ProviderEventHandler.getInstance();